import { useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Printer } from "lucide-react";
import { Button } from "../../../Components/ui/buttons/button";
import "../../../Components/PrintContainer/print.css";
import { ProjecaoReportBody } from "./ProjecaoReportBody";
import type { ProjecaoReportData } from "./types";

interface ProjecaoReportPreviewProps {
  isOpen: boolean;
  reportData: ProjecaoReportData | null;
  onClose: () => void;
}

export function ProjecaoReportPreview({
  isOpen,
  reportData,
  onClose,
}: ProjecaoReportPreviewProps) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = () => {
    setIsPrinting(true);
    // Give the print-container portal a tick to render before the browser
    // snapshots the page for the print dialog.
    setTimeout(() => {
      let fallbackTimer: ReturnType<typeof setTimeout>;
      const cleanup = () => {
        clearTimeout(fallbackTimer);
        setIsPrinting(false);
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      // If the print dialog is blocked or never opens, "afterprint" never
      // fires and the button would stay stuck on "Imprimindo...".
      fallbackTimer = setTimeout(cleanup, 10000);
      window.print();
    }, 100);
  };

  if (!isOpen || !reportData) return null;

  const printContainer = document.getElementById("print-container");

  return (
    <>
      <div
        className="fixed inset-0 z-modal-overlay bg-black bg-opacity-50"
        onClick={onClose}
      />

      <div className="fixed inset-0 z-modal flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-lg shadow-2xl pointer-events-auto w-[90%] max-w-[1200px] h-[90vh] flex flex-col">
          <div className="bg-white border-b border-gray-300 px-6 py-4 flex justify-between items-center rounded-t-lg flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-800">
              Relatório da projeção customizada
            </h2>

            <div className="flex items-center gap-3">
              <Button
                onClick={handlePrint}
                disabled={isPrinting}
                className="px-6 py-2.5 h-auto bg-emerald-700 text-white hover:bg-emerald-800 font-medium shadow-sm"
              >
                {isPrinting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Imprimindo...
                  </>
                ) : (
                  <>
                    <Printer size={20} />
                    Imprimir / Salvar PDF
                  </>
                )}
              </Button>

              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-white p-6">
            <div className="max-w-[21cm] bg-white mx-auto shadow-[0_0_20px_rgba(0,0,0,0.1)]">
              <div className="py-[2cm] px-[1.5cm] bg-white box-border">
                <ProjecaoReportBody data={reportData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isPrinting &&
        printContainer &&
        createPortal(
          <div className="print-content">
            <div className="print-page">
              <ProjecaoReportBody data={reportData} />
            </div>
          </div>,
          printContainer
        )}
    </>
  );
}
